// The CRM's import cache screen (#1562): look up the shared Smart recipe a link or text is served from
// (smart_recipe_cache), purge it, join a link to another as the same recipe, or undo a join. Every change goes
// through the `adminSmartImport` callable; the screen reads nothing until an admin looks something up.

import { BADGE, BADGE_DOT, BUTTON, CARD, EMPTY_STATE, FIELD, TABLE } from "./ui/classes.js";
import { icon, solidIcon } from "./ui/icons.js";
import { escapeHTML, safeUrl } from "./familyDetailsPanel.js";

let ctx = null;
let lastQuery = null;

const STATUS_BADGES = {
  ready: `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Ready</span>`,
  failed: `<span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Failed</span>`,
  processing: `<span class="${BADGE.yellow}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Being processed</span>`,
  absent: `<span class="${BADGE.gray}"><span aria-hidden="true" class="${BADGE_DOT.gray}"></span>Expired</span>`
};
const SOURCES = { admin: "an admin, in the CRM", redirect: "a redirect", canonical: "the page's own address", device: "a phone that followed it" };

function when(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function link(url, fallback = "—") {
  const safe = safeUrl(url);
  return safe ? `<a href="${escapeHTML(safe)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link} break-all">${escapeHTML(safe)}</a>` : escapeHTML(fallback);
}

/** What the query is: a single link, or recipe text. */
export function queryOf(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  return /^https?:\/\/\S+$/i.test(trimmed) ? { url: trimmed } : { text: trimmed };
}

/** A lookup's result as the screen shows it. Pure, for tests/admin/importCache.test.js. */
export function lookupResultHTML(result, query) {
  const entry = result.entry;
  const aliasBanner = result.alias
    ? `<div class="flex flex-wrap items-center justify-between gap-3 rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:bg-blue-500/10 dark:text-blue-200">
         <span>This link is the same recipe as ${link(result.alias.target_url, result.alias.target_key)}, joined by ${escapeHTML(SOURCES[result.alias.source] || result.alias.source || "an alias")}.</span>
         <button type="button" data-cache-remove-alias data-alias-key="${escapeHTML(result.key)}" class="${BUTTON.small}">${solidIcon("x-mark", "size-4 text-gray-400")}Remove the join</button>
       </div>`
    : "";

  const entryHTML = entry
    ? `<div class="${CARD.panel}">
         <div class="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
           <div class="min-w-0">
             <h3 class="${CARD.title}">${escapeHTML(entry.title || entry.failure_reason || "No title")}</h3>
             <p class="mt-1 truncate font-mono text-xs text-gray-500 dark:text-gray-400">${escapeHTML(entry.key)}</p>
           </div>
           <div class="flex items-center gap-2">${STATUS_BADGES[entry.status] || `<span class="${BADGE.gray}">${escapeHTML(entry.status)}</span>`}
             <button type="button" data-cache-purge class="${BUTTON.smallDanger}"${entry.status === "processing" ? " disabled" : ""}>${solidIcon("trash", "size-4")}Purge</button>
           </div>
         </div>
         <dl class="grid grid-cols-1 gap-x-6 gap-y-4 px-4 py-5 sm:grid-cols-3 sm:px-6">
           <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">From</dt><dd class="mt-1 text-sm text-gray-900 dark:text-white">${entry.kind === "text" ? "Pasted text" : link(entry.canonical_url)}</dd></div>
           <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Served</dt><dd class="mt-1 text-sm text-gray-900 dark:text-white">${entry.hit_count} time${entry.hit_count === 1 ? "" : "s"}, last ${escapeHTML(when(entry.last_hit_at))}</dd></div>
           <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Kept until</dt><dd class="mt-1 text-sm text-gray-900 dark:text-white">${entry.expires_at ? escapeHTML(when(entry.expires_at)) : "For good"}</dd></div>
           <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Read by</dt><dd class="mt-1 text-sm text-gray-900 dark:text-white">${escapeHTML(entry.origin?.read_by || "—")}</dd></div>
           <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">First asked by</dt><dd class="mt-1 font-mono text-xs break-all text-gray-900 dark:text-white">${escapeHTML(entry.origin?.user_id || "—")}<div class="text-gray-500 dark:text-gray-400">${escapeHTML(entry.origin?.family_id || "")}</div></dd></div>
           <div><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Made</dt><dd class="mt-1 text-sm text-gray-900 dark:text-white">${escapeHTML(when(entry.created_at))}${entry.pipeline_version ? ` · pipeline ${escapeHTML(entry.pipeline_version)}` : ""}</dd></div>
           ${entry.failure_reason ? `<div class="sm:col-span-3"><dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Why it failed</dt><dd class="mt-1 text-sm text-red-700 dark:text-red-400">${escapeHTML(entry.failure_reason)}</dd></div>` : ""}
         </dl>
       </div>`
    : `<div class="${EMPTY_STATE.box}">
         ${icon("archive-box", EMPTY_STATE.icon)}
         <h3 class="${EMPTY_STATE.title}">No recipe for that</h3>
         <p class="${EMPTY_STATE.text}">Nothing has imported it yet, or its recipe expired. The next import makes it.</p>
       </div>`;

  const aliasRows = (result.aliases || []).map((a) => `
    <tr>
      <td class="${TABLE.td} max-w-md whitespace-normal">${a.from_url ? link(a.from_url) : `<span class="font-mono text-xs">${escapeHTML(a.key)}</span>`}</td>
      <td class="${TABLE.td}">${escapeHTML(SOURCES[a.source] || a.source || "—")}</td>
      <td class="${TABLE.td}">${escapeHTML(when(a.created_at))}</td>
      <td class="${TABLE.td} text-right"><button type="button" data-cache-remove-alias data-alias-key="${escapeHTML(a.key)}" class="${BUTTON.small}">Remove</button></td>
    </tr>`).join("");
  const aliasesHTML = aliasRows
    ? `<div class="${CARD.panel}"><div class="px-4 py-5 sm:px-6">
         <h3 class="${CARD.title}">Links that are the same recipe</h3>
         <div class="relative mt-4 overflow-x-auto"><table class="${TABLE.table}"><thead><tr><th class="${TABLE.th}">Link</th><th class="${TABLE.th}">Joined by</th><th class="${TABLE.th}">When</th><th class="${TABLE.th}"><span class="sr-only">Remove</span></th></tr></thead><tbody class="${TABLE.tbody}">${aliasRows}</tbody></table></div>
       </div></div>`
    : "";

  const joinHTML = query && query.url
    ? `<div class="${CARD.panel}"><div class="px-4 py-5 sm:px-6">
         <h3 class="${CARD.title}">Same recipe as another link</h3>
         <p class="${CARD.description}">Make this link serve the recipe of another one, such as a video and the blog post it was made from. Imports waiting on this link are served at once if the other has a recipe.</p>
         <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
           <div class="sm:col-span-2">
             <label for="importCacheJoinMain" class="${FIELD.label}">The other link</label>
             <input type="url" id="importCacheJoinMain" placeholder="https://…" class="mt-2 ${FIELD.input}">
           </div>
           <div>
             <label for="importCacheJoinKeep" class="${FIELD.label}">If both have a recipe, keep</label>
             <div class="mt-2 grid grid-cols-1">
               <select id="importCacheJoinKeep" class="${FIELD.select}"><option value="main">The other link's</option><option value="link">This link's</option></select>
               ${solidIcon("chevron-down", FIELD.selectChevron)}
             </div>
           </div>
         </div>
         <div class="mt-4 flex justify-end"><button type="button" data-cache-join class="${BUTTON.secondary}">${solidIcon("link", "size-5 text-gray-400")}Join them</button></div>
       </div></div>`
    : "";

  return `<div class="flex flex-col gap-6">${aliasBanner}${entryHTML}${aliasesHTML}${joinHTML}</div>`;
}

async function call(data) {
  const { functions, httpsCallable } = ctx;
  return (await httpsCallable(functions, "adminSmartImport")(data)).data;
}

async function lookUp(query) {
  const area = document.getElementById("importCacheResult");
  if (!area || !query) return;
  lastQuery = query;
  area.innerHTML = `<p class="text-sm text-gray-500 dark:text-gray-400">Looking it up…</p>`;
  try {
    const result = await call({ action: "lookup", ...query });
    area.innerHTML = lookupResultHTML(result, query);
  } catch (err) {
    console.error("❌ adminSmartImport lookup failed:", err);
    area.innerHTML = `<p class="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Couldn't look it up: ${escapeHTML(err.message)}</p>`;
  }
}

async function act(btn, data, done) {
  btn.disabled = true;
  try {
    const result = await call(data);
    ctx.showToast?.(done(result), "success");
    await lookUp(lastQuery);
  } catch (err) {
    console.error(`❌ adminSmartImport ${data.action} failed:`, err);
    ctx.showToast?.(`That didn't work: ${err.message}`, "error");
    btn.disabled = false;
  }
}

export async function loadImportCacheTemplate() {
  const container = document.getElementById("sectionImportCacheContainer");
  if (!container) return;
  try {
    const res = await fetch("templates/importCache.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load importCache.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the import cache template: ${escapeHTML(err.message)}</div>`;
  }
}

/** Binds the screen's controls. Reads nothing: a lookup is the admin's (platform/CLAUDE.md, every screen mounts signed out). */
export function initImportCacheController(context) {
  ctx = context;
  const form = document.getElementById("importCacheForm");
  const input = document.getElementById("importCacheQuery");
  const area = document.getElementById("importCacheResult");
  if (!form || !input || !area) return;

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const query = queryOf(input.value);
    if (!query) return ctx.showToast?.("Paste a link or a recipe's text first.", "warning");
    lookUp(query);
  });

  area.addEventListener("click", (e) => {
    const purge = e.target.closest("[data-cache-purge]");
    const remove = e.target.closest("[data-cache-remove-alias]");
    const join = e.target.closest("[data-cache-join]");
    if (purge) {
      if (!window.confirm("Purge this recipe? Households that already have it keep their copy; the next import makes it again.")) return;
      act(purge, { action: "purge", ...lastQuery }, (r) => `Purged${r.deleted_aliases ? `, with ${r.deleted_aliases} join${r.deleted_aliases === 1 ? "" : "s"}` : ""}.`);
    } else if (remove) {
      act(remove, { action: "remove_alias", key: remove.dataset.aliasKey }, (r) => (r.removed ? "Join removed." : "That join was already gone."));
    } else if (join) {
      const main = document.getElementById("importCacheJoinMain")?.value.trim();
      const keep = document.getElementById("importCacheJoinKeep")?.value || "main";
      if (!main) return ctx.showToast?.("Paste the other link first.", "warning");
      act(join, { action: "join", link: lastQuery.url, main, keep },
        (r) => `Joined.${r.moved_recipe ? " The recipe moved to the other link." : ""}${r.served_imports ? ` ${r.served_imports} waiting import${r.served_imports === 1 ? "" : "s"} served.` : ""}`);
    }
  });
}
